import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import { SelectModule } from '@openng/optimus-ui/select';
import {
  Subject,
  takeUntil
} from 'rxjs';
import { CampaignSelection } from '../../models/campaign/campaign-selection';
import { ICampaign } from '../../models/campaign/campaign.interface';
import { CampaignService } from '../../services/campaign.service';
import { Helper } from '../../utils/helper';

@Component({
    selector: 'app-home',
    imports: [FormsModule, SelectModule, RouterModule, AutoCompleteModule, CommonModule],
    templateUrl: './home.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly campaignService = inject(CampaignService);

  // Campaign name suggestions
  protected suggestions: CampaignSelection[] | undefined;
  private _destroy$ = new Subject<void>();

  campaignsCopy: CampaignSelection[] | undefined;
  selectedCampaign: CampaignSelection | undefined;

  loading = true;

  ngOnDestroy(): void {
    // Used for unsubscribing on destroy
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnInit() {
    this.campaignService
      .getCampaignList({ first: 0, rows: 10, search: undefined })
      .pipe(takeUntil(this._destroy$))
      .subscribe((campaigns: ICampaign[]) => {
        const mappedCampaigns = this.MapCampaignsToSelectionArray(campaigns);
        this.suggestions = mappedCampaigns;
        this.campaignsCopy = mappedCampaigns;
        this.loading = false;
      });
  }

  private MapCampaignsToSelectionArray(campaigns: ICampaign[]): CampaignSelection[] | undefined {
    return campaigns
      ? campaigns.map((campaign: ICampaign) => ({
        id: campaign.campaignId!,
        name: campaign.campaignName,
        hint: campaign.campaignName,
      }))
      : undefined;
  }

  searchCampaign(event: AutoCompleteCompleteEvent) {
    if (event.query === '') {
      // Reset search suggestions to default
      this.suggestions = [...this.campaignsCopy!];
    }
    else {
      this.campaignService.getCampaignList({ first: 0, rows: 10, search: event.query }).subscribe(
        {
          next: (campaigns) => {
            this.suggestions = [...this.MapCampaignsToSelectionArray(campaigns)!];

            // selectedCampaign here is sometimes just a string, not an object
            if (!this.selectedCampaign?.name) {
              this.selectedCampaign = {
                id: this.selectedCampaign?.id!,
                name: this.selectedCampaign!.toString(),
                hint: this.selectedCampaign!.toString(),
              };
            }

            // If no campaign is found, add an option to create the table with this name
            if (this.suggestions?.length == 0) {
              this.selectedCampaign!.name = Helper.removeSpecialCharacters(
                this.selectedCampaign!.name
              );
              this.suggestions[0] = {
                id: this.selectedCampaign!.id!,
                name: this.selectedCampaign!.name,
                hint: `No campaign found! Create a campaign with "${this.selectedCampaign!.name
                  }" name?`,
              };
            } else if (!this.suggestions?.find(s => s.name === this.selectedCampaign!.name)) {
              this.suggestions?.push({
                id: this.selectedCampaign!.id!,
                name: this.selectedCampaign!.name,
                hint: `No campaign found! Create a campaign with "${this.selectedCampaign!.name
                  }" name?`,
              });
            }
          }
        }
      );
    }
  }

  routeToCampaign() {

    if (!this.selectedCampaign?.id) {

      // Create a new campaign
      this.router.navigate(['/campaign/new'], { state: { campaignName: this.selectedCampaign?.name } })

    } else {

      // Route to existing campaign
      this.router.navigate(['/campaign/edit'], {
        state: {
          campaignData: {
            campaignName: this.selectedCampaign?.name,
            campaignId: this.selectedCampaign?.id
          }
        }
      });
    }
  }
}
